'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { AlertCircle, Eye, EyeOff, Lock, Mail, Music2, Sparkles, User, X } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { Deezer, Google, Spotify, YoutubeMusic } from '@/app/components/Icons'
import Button from '@/app/components/ui/Button'
import CustomCheckbox from '@/app/components/ui/Checkbox'

const loginSchema = z.object({
  identifier: z
    .string()
    .min(1, 'Campo obrigatório'),
  password: z.string().min(1, 'Campo obrigatório'),
})

type LoginFormInputs = z.infer<typeof loginSchema>

// Credenciais válidas
const VALID_CREDENTIALS = {
  users: ['admin', 'admin@7sete.com', 'admin@teste.com', 'admin@simple.com', 'admin@admin.com', 'admin@curator.com', 'admin@simplified.com', 'admin@distribution.com'],
  passwords: {
    'admin': '36912',
    'admin@7sete.com': '36912',
    'admin@teste.com': 'TempPassword123!',
    'admin@simple.com': '4812',
    'admin@admin.com': 'TempPassword123@@',
    'admin@curator.com': 'TempPassword123@@',
    'admin@simplified.com': 'TempPassword123@@',
    'admin@distribution.com': 'TempPassword123@@',
  }
}

export default function SignIn() {
  const [rememberMe, setRememberMe] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showIntegrationModal, setShowIntegrationModal] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState('')

  const handleSocialLogin = async (provider: string) => {
    if (provider === 'Spotify') {
      try {
        // IMPORTANTE: Usar spotifyApi.login() para criar o verifier corretamente
        // Não fazer redirect manual - isso não cria o verifier necessário para PKCE
        const { spotifyApi } = await import('@/services/spotifyApiService')
        spotifyApi.login()
        // O login redireciona automaticamente
        return
      } catch (error) {
        console.error('Erro ao conectar com Spotify:', error)
        toast.error('Erro ao conectar com Spotify')
        return
      }
    }
    setSelectedProvider(provider)
    setShowIntegrationModal(true)
  }

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginFormInputs>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit: SubmitHandler<LoginFormInputs> = async data => {
    // Validar credenciais
    const identifier = data.identifier.toLowerCase()
    const isValidUser = VALID_CREDENTIALS.users.includes(identifier)

    // Pega a senha correta para o usuário
    const correctPassword = VALID_CREDENTIALS.passwords[identifier as keyof typeof VALID_CREDENTIALS.passwords]
    const isValidPassword = correctPassword && data.password === correctPassword

    if (!isValidUser || !isValidPassword) {
      setError('identifier', { message: '' })
      setError('password', { message: 'Credenciais inválidas' })
      toast.error('Credenciais inválidas')
      return
    }

    // Login válido
    const response = await fetch('../../api/sign-in', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: data.identifier, password: data.password }),
    })

    if (response.ok) {
      toast.success('Login realizado com sucesso!', { id: 'sign-in-success' })

      // Redirecionar por role
      const curatorEmails = ['admin@teste.com', 'admin@curator.com']
      const simplifiedEmails = ['admin@simple.com', 'admin@simplified.com']
      let redirectUrl = '/home'
      if (curatorEmails.includes(identifier)) redirectUrl = '/curator'
      else if (simplifiedEmails.includes(identifier)) redirectUrl = '/simplified'
      else if (identifier === 'admin@distribution.com') redirectUrl = '/distribution'
      window.location.href = redirectUrl
    } else {
      toast.error('Erro ao realizar login')
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Lado esquerdo - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-neutral-900">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-sky-600/8 rounded-full blur-[100px]" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-600/6 rounded-full blur-[100px]" />

        <div className="relative z-10 flex flex-col justify-between items-center w-full p-12 py-16">
          {/* Top spacer */}
          <div />

          {/* Center content */}
          <div className="text-center">
            <div className="mb-12">
              <Image
                src="/assets/logos/logo-branco-simples.svg"
                alt="7Sete Songs"
                width={80}
                height={80}
                priority
                className="mx-auto opacity-90"
              />
            </div>

            <h1 className="text-7xl xl:text-8xl font-black text-white tracking-[-0.06em] leading-[0.85]">
              7Sete
            </h1>
            <p className="text-3xl xl:text-4xl font-extralight text-neutral-300 tracking-[0.35em] uppercase mt-3">
              Songs
            </p>

            <div className="flex items-center gap-4 justify-center mt-10 mb-0">
              <div className="w-16 h-px bg-gradient-to-r from-transparent to-sky-500/30" />
              <div className="w-1.5 h-1.5 rounded-full bg-sky-500/40" />
              <div className="w-16 h-px bg-gradient-to-l from-transparent to-sky-500/30" />
            </div>

            <div className="mt-10 space-y-3">
              <p className="text-lg xl:text-xl font-extrabold text-sky-400 tracking-[0.25em] uppercase">
                Gestão &amp; Distribuição
              </p>
              <p className="text-5xl xl:text-6xl font-black text-white tracking-tight">
                Musical
              </p>
            </div>
          </div>

          {/* Bottom brand */}
          <p className="text-[11px] text-neutral-700 tracking-[0.2em] uppercase">
            Music Distribution Platform
          </p>
        </div>
      </div>

      {/* Lado direito - Formulário */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-10 sm:p-10 bg-[#0a0a0f] min-h-screen lg:min-h-0">
        <div className="w-full max-w-[440px]">
          {/* Logo + brand mobile */}
          <div className="lg:hidden mb-12 text-center">
            <Image
              src="/assets/logos/logo-branco-simples.svg"
              alt="7Sete Songs"
              width={64}
              height={64}
              priority
              className="mx-auto mb-6"
            />
            <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tighter">7Sete</h1>
            <p className="text-lg sm:text-xl font-extralight text-neutral-400 tracking-[0.3em] uppercase mt-1.5">Songs</p>
          </div>

          {/* Header */}
          <div className="mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3 tracking-tight">Bem-vindo de volta</h2>
            <p className="text-neutral-500 text-base">Entre com suas credenciais para continuar</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} method="POST" className="space-y-5" suppressHydrationWarning>
            {/* Email/Username */}
            <div>
              <label htmlFor="identifier" className="block text-sm font-semibold text-neutral-400 mb-2.5 uppercase tracking-wider">
                Email ou Usuário
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-600">
                  <Mail className="w-5 h-5" />
                </div>
                <input
                  {...register('identifier')}
                  type="text"
                  id="identifier"
                  name="identifier"
                  suppressHydrationWarning
                  autoComplete="username"
                  placeholder="seu@email.com"
                  className={`w-full pl-12 pr-4 py-3.5 sm:py-4 bg-white/[0.04] border rounded-xl text-white text-base placeholder-neutral-600 focus:outline-none focus:ring-2 transition-all duration-200 ${
                    errors.identifier
                      ? 'border-red-500/60 focus:ring-red-500/20'
                      : 'border-neutral-800/80 focus:ring-sky-500/20 focus:border-sky-500/50 hover:border-neutral-700'
                  }`}
                />
              </div>
              {errors.identifier?.message && (
                <p className="mt-2 text-sm text-red-400">{errors.identifier.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-neutral-400 mb-2.5 uppercase tracking-wider">
                Senha
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-600">
                  <Lock className="w-5 h-5" />
                </div>
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  suppressHydrationWarning
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className={`w-full pl-12 pr-14 py-3.5 sm:py-4 bg-white/[0.04] border rounded-xl text-white text-base placeholder-neutral-600 focus:outline-none focus:ring-2 transition-all duration-200 ${
                    errors.password
                      ? 'border-red-500/60 focus:ring-red-500/20'
                      : 'border-neutral-800/80 focus:ring-sky-500/20 focus:border-sky-500/50 hover:border-neutral-700'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-600 hover:text-sky-400 transition-colors duration-200"
                  title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password?.message && (
                <p className="mt-2 text-sm text-red-400">{errors.password.message}</p>
              )}
            </div>

            {/* Remember & Forgot */}
            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2.5 cursor-pointer group">
                <CustomCheckbox
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                />
                <span className="text-sm text-neutral-400 group-hover:text-neutral-200 transition-colors">Lembrar de mim</span>
              </label>
              <Link
                href="/forgot-password"
                className="text-sm text-sky-400/80 hover:text-sky-300 transition-colors font-medium"
              >
                Esqueceu a senha?
              </Link>
            </div>

            {/* Submit */}
            <div className="pt-3">
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting}
                className="w-full py-3.5 sm:py-4 text-base bg-sky-600 hover:bg-sky-500 active:bg-sky-700 text-white font-bold rounded-xl transition-all duration-200 disabled:opacity-75 shadow-lg shadow-sky-600/20 hover:shadow-sky-500/30"
              >
                Entrar
              </Button>
            </div>
          </form>

          {/* Sign up */}
          <div className="mt-10 pt-6 border-t border-neutral-800/40">
            <p className="text-center text-base text-neutral-500">
              Não tem uma conta?{' '}
              <Link
                href="/sign-up"
                className="text-sky-400 hover:text-sky-300 font-semibold transition-colors"
              >
                Criar conta
              </Link>
            </p>
          </div>

          <p className="mt-6 text-center text-xs text-neutral-600 leading-relaxed">
            Ao entrar, você aceita os{' '}
            <Link href="/terms" className="text-neutral-500 hover:text-sky-400 transition-colors underline underline-offset-2">
              Termos de Serviço
            </Link>{' '}
            e a{' '}
            <Link href="/privacy" className="text-neutral-500 hover:text-sky-400 transition-colors underline underline-offset-2">
              Política de Privacidade
            </Link>
          </p>
        </div>
      </div>

      {/* Modal de integração não disponível */}
      {showIntegrationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowIntegrationModal(false)}
          />

          {/* Modal */}
          <div className="relative bg-neutral-900 border border-neutral-800 rounded-lg p-6 max-w-sm w-full shadow-lg">
            {/* Close button */}
            <button
              onClick={() => setShowIntegrationModal(false)}
              className="absolute top-4 right-4 text-neutral-500 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Content */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-amber-500/10 rounded-full flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-amber-500" />
              </div>

              <h3 className="text-xl font-bold text-white mb-2">
                Integração Indisponível
              </h3>

              <p className="text-neutral-400 mb-6">
                O login com <span className="text-white font-medium">{selectedProvider}</span> ainda não está disponível.
                Estamos trabalhando para adicionar essa funcionalidade em breve!
              </p>

              <button
                onClick={() => setShowIntegrationModal(false)}
                className="w-full py-3 bg-neutral-800 hover:bg-neutral-700 text-white font-medium rounded-lg transition-colors cursor-pointer"
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
